import { Component, EventEmitter, inject, Inject, Input, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CateringAggregationArticleViewModel } from 'libs/core/src/lib/model/catering/catering-aggregation.view.model';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { ScreeningItemViewModel } from 'libs/core/src/lib/model/view-model/order/screening-item/screening-item.view.model';
import { VoucherTypeModel } from 'libs/core/src/lib/model/voucher-type.model';
import { TotalizerService } from 'libs/core/src/lib/service/totalizer.service';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { ENVIRONMENT_TOKEN } from 'libs/core/src/public-api';
import { OrderSummaryBuilder } from 'libs/core/src/lib/model/page/order/order-summary.builder';
import { OrderSummaryViewModel } from 'libs/core/src/lib/model/page/order/order-summary.view.model';
import { VoucherViewModel } from 'libs/core/src/lib/model/view-model/voucher/voucher.view.model';
import { TicketViewModel } from 'libs/core/src/lib/model/view-model/shared/ticket/ticket.view.model';
import { AppService } from 'libs/core/src/lib/service/app.service';
import { OrderSummaryArticleCombinationItemViewModel } from 'libs/core/src/lib/model/page/order/order-summary-article-combination-item.view.model';
import { ExtraFeeModel } from '../../basket/basket-fee/model/basket.fee.model';

@Component({
  template: ''
})
export class OrderTicketsSummaryComponent {
  protected environment: any = inject(ENVIRONMENT_TOKEN);

  @Output()
  public articleCombinationQuantityChange: EventEmitter<CateringAggregationArticleViewModel> = new EventEmitter<CateringAggregationArticleViewModel>();

  @Input()
  screeningId: string = null;

  @Input()
  public isLoading = false;

  @Input()
  public couldChangeOptionalFees = false;

  @Output()
  public recalculate: EventEmitter<OrderSummaryViewModel> = new EventEmitter<OrderSummaryViewModel>();

  public orderSummaryView: OrderSummaryViewModel = null;

  public pickUpTime: DateTime;
  public periodBetweenRequestsInScreen = 500;
  private readonly forceOptionalFees: boolean;
  private totalDiscount = 0;
  private _ticketList: Array<TicketViewModel> = null;
  private _voucherTypeList: Array<VoucherTypeModel> = null;
  private _cateringArticleCombinationList: Array<CateringAggregationArticleViewModel> = null;

  private debouncer: Subject<void> = new Subject<void>();
  private buildOrderSummaryViewDebouncer: Subject<void> = new Subject<void>();

  constructor(
    protected route: ActivatedRoute,
    protected translateService: TranslateService,
    protected orderStateService: OrderStateService,
    protected totalizerService: TotalizerService,
    protected appService: AppService,
    protected orderSummaryBuilder: OrderSummaryBuilder
  ) {
    this.forceOptionalFees = this.environment.constants.forceOptionalFees;
    this.periodBetweenRequestsInScreen = this.environment.screen.periodBetweenRequestsInScreen;
    this.buildOrderSummaryViewDebouncer.pipe(debounceTime(100)).subscribe(() => this.buildOrderSummaryView());
  }

  private _order: OrderViewModel = null;
  public get order() {
    return this._order;
  }

  @Input()
  public set order(value: OrderViewModel) {
    this._order = value;
    this.pickUpTime = value?.pickupTime;

    this.buildOrderSummaryViewDebouncer.next();
  }

  @Input()
  public set ticketList(ticketList: Array<TicketViewModel>) {
    this._ticketList = ticketList;
    if (ticketList) {
      this.buildOrderSummaryViewDebouncer.next();
    }
  }

  private isFirstTime(): boolean {
    return this.order && this.order.screeningItems?.every((item) => item.ticketPrice === 0);
  }

  @Input()
  public set voucherTypeList(voucherTypeList: Array<VoucherTypeModel>) {
    this._voucherTypeList = voucherTypeList;
    this.buildOrderSummaryViewDebouncer.next();
  }

  @Input()
  public set cateringArticleCombinationList(articleCombinationList: Array<CateringAggregationArticleViewModel>) {
    this._cateringArticleCombinationList = articleCombinationList;

    this.buildOrderSummaryViewDebouncer.next();
  }

  @Input()
  public set initExtraFeeList(extraFeeIdentifierList: Array<Array<string>>) {
    if (this.orderSummaryView && extraFeeIdentifierList.length > 0) {
      const availableOptionalExtraFeeList: ExtraFeeModel[] = this.orderSummaryView.optionalExtraFees;

      this.order.screeningItems.forEach((item) => {
        item.optionalExtraFees = [];
      });

      for (const extraFeeIdentifierPair of extraFeeIdentifierList) {
        const optionalExtraFeeViewModel: ExtraFeeModel = availableOptionalExtraFeeList.find((x) => x.id === extraFeeIdentifierPair[1]);
        if (!optionalExtraFeeViewModel) {
          continue;
        }
        const foundOrderItemModel = this.order.screeningItems.find((item: ScreeningItemViewModel) => {
          return item.ticketId === extraFeeIdentifierPair[0] && !item.optionalExtraFees.includes(optionalExtraFeeViewModel.id);
        });
        if (foundOrderItemModel) {
          foundOrderItemModel.optionalExtraFees = [];
          foundOrderItemModel.optionalExtraFees.push(optionalExtraFeeViewModel.id);
        }
      }
    }
  }

  private _appliedVoucherList: VoucherViewModel[] = [];
  public get appliedVoucherList() {
    return this._appliedVoucherList;
  }

  public set appliedVoucherList(appliedVoucherList: VoucherViewModel[]) {
    this._appliedVoucherList = appliedVoucherList;
    if (appliedVoucherList && appliedVoucherList.length > 0) {
      this.totalDiscount = appliedVoucherList.reduce((x, y) => (x += y.positions[0].discount), 0);
      this.buildOrderSummaryViewDebouncer.next();
    }
  }

  public doDecrement(extraFee: ExtraFeeModel) {
    const foundOrderItemModel = this.order.screeningItems.find((item: ScreeningItemViewModel) => {
      return extraFee.ticketIds.includes(item.ticketId) && item.optionalExtraFees.includes(extraFee.id);
    });

    if (foundOrderItemModel !== undefined) {
      const index = foundOrderItemModel.optionalExtraFees.indexOf(extraFee.id);
      if (index > -1) {
        foundOrderItemModel.optionalExtraFees.splice(index, 1);
      }

      this.debouncer.next();
    }
  }

  public doIncrement(extraFee: ExtraFeeModel) {
    // Znajdź zamówienie bez optionalExtraFees
    const foundOrderItemModel = this.order.screeningItems.find((item: ScreeningItemViewModel) => {
      return extraFee.ticketIds.includes(item.ticketId) && !item.optionalExtraFees.includes(extraFee.id);
    });

    if (foundOrderItemModel !== undefined) {
      foundOrderItemModel.optionalExtraFees.push(extraFee.id);
      this.debouncer.next();
    }
  }

  private emitQuantityChanged(item: OrderSummaryArticleCombinationItemViewModel) {
    this.articleCombinationQuantityChange.emit(item.articleCombination);
    this.buildOrderSummaryViewDebouncer.next();
    this.recalculate.next(this.orderSummaryView);
  }

  public onDecrementCatering(item: OrderSummaryArticleCombinationItemViewModel): void {
    if (item.quantity === 0) {
      return;
    }
    item.quantity--;
    item.articleCombination.selectedQuantity--;
    this.emitQuantityChanged(item);
  }

  public onIncrementCatering(item: OrderSummaryArticleCombinationItemViewModel): void {
    item.quantity++;
    item.articleCombination.selectedQuantity++;
    this.emitQuantityChanged(item);
  }

  getTotalOptionalExtraFee(): number {
    let sum = 0.0;

    if (this.orderSummaryView?.optionalExtraFees != null) {
      this.orderSummaryView.optionalExtraFees.forEach((item) => {
        sum += item.price * this.order.getCountOptionalFeeByGroupFee(item.id);
      });
    }

    return sum;
  }

  public getTotalPrice(): number {
    let sum = 0.0;

    if (this.orderSummaryView) {
      this.orderSummaryView.items.forEach((element) => {
        sum += element.ticket.priceWithExtraFee * element.quantity;
      });

      this.orderSummaryView.voucherItems.forEach((element) => {
        sum += element.voucher.price * element.quantity;
      });

      this.orderSummaryView.cateringItems.forEach((element) => {
        sum += element.unitPriceWithSeparatedItems * element.quantity;
      });
    }

    sum += this.getTotalOptionalExtraFee();
    return sum;
  }

  private forcingOptionalFees(): void {
    this.order.screeningItems.forEach((screeningItem) => {
      const optionalExtraFeeIds = [];
      this.orderSummaryView.optionalExtraFees.forEach((optionalExtraFee) => {
        if (optionalExtraFee.ticketIds.includes(screeningItem.ticketId)) {
          optionalExtraFeeIds.push(optionalExtraFee.id);
        }
      });

      screeningItem.optionalExtraFees = optionalExtraFeeIds;
    });
  }

  private buildOrderSummaryView(): void {
    const order = this._order ?? this.orderStateService.getOrder();
    if (!order) {
      return;
    }

    this._order = order;
    const ticketList: TicketViewModel[] = this._ticketList ?? [];
    const voucherTypeList: VoucherTypeModel[] = this._voucherTypeList || [];
    const cateringArticleCombinationList: CateringAggregationArticleViewModel[] = this._cateringArticleCombinationList
      ? this._cateringArticleCombinationList.sort((a, b) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        })
      : [];

    const orderSummaryView = this.orderSummaryBuilder.getOrderSummary(
      this._order,
      ticketList,
      voucherTypeList,
      cateringArticleCombinationList,
      this.screeningId
    );

    if (orderSummaryView) {
      orderSummaryView.items.forEach((item) => {
        item.seatIds.forEach((seatId) => {
          if (order.screeningItems.find((x) => x.seatId === seatId) === undefined) {
            const screeningItem = new ScreeningItemViewModel();
            screeningItem.ticketId = item.ticket.id;
            screeningItem.seatId = seatId;
            screeningItem.screeningId = this.screeningId;
            order.screeningItems.push(screeningItem);
          }
        });
      });

      if (order?.defaultExtraFees?.length > 0) {
        orderSummaryView.totalPrice += order.defaultExtraFees[0].defaultExtraFeePrice;
      }

      this.orderSummaryView = orderSummaryView;
      if (this.forceOptionalFees && this.route.snapshot.data['pageIdentify'] === 'basket' && this.isFirstTime()) {
        this.forcingOptionalFees();
      }
    }

    this.recalculate.next(this.orderSummaryView);
  }
}
